import { useCallback, useContext, cloneElement } from "react";
import { Provider as ReakitStylesProvider } from "reakit";
import * as reakitSystemStyles from "reakit-system-bootstrap";
import {
  Tooltip as ReakitTooltip,
  TooltipReference,
  TooltipArrow,
  useTooltipState
} from "reakit/Tooltip";
import { isEmpty, omit } from "lodash";
import classNames from "classnames";

import { ModalContext } from "../Modals/Modal";
import { getIsTouchDevice } from "../Utils/ResponsiveQueries";

const Tooltip = ({
  show = true,
  forceDisplay = false,
  placement = "auto",
  clickable = false,
  appearFrom = false,
  acceptHTMLContent = false,
  enableOnTouch = true,
  ...props
}) => {
  const { content, children } = props;

  // Define whether the tooltip is shown inside a modal, by checking if there's a modal
  // context available.
  const inModal = !isEmpty(useContext(ModalContext));

  const forcedTooltipProps = {};
  if (forceDisplay) forcedTooltipProps.visible = true;

  const animated = !!appearFrom && 250;

  const initialTooltipProps = {
    placement,
    unstable_fixed: false,
    animated,
    ...forcedTooltipProps
  };

  // The `unstable_fixed` prop allows the tooltip to move when scrolling the page,
  // otherwise its default behavior is to stay fixed at the same position on the screen
  // while it's still shown, even if the element moved in the page.
  const tooltipProps = useTooltipState(initialTooltipProps);

  // Avoid clicking tooltip to dispatch a click event to it's parent container as tooltips
  // are most of the time independent element, and its interactions are scoped to them.
  const handleTooltipClicked = useCallback(e => e.stopPropagation(), []);

  if (!show || !content || (!enableOnTouch && getIsTouchDevice())) return children;

  const className = classNames(props.className, {
    "modal-tooltip": inModal,
    "app-tooltip": !inModal,
    "clickable-tooltip": clickable
  });

  const referenceClassName = classNames("tooltip-reference", children.props.className);

  const tooltipInnerClassName = classNames("tooltip-inner", {
    "tooltip-appear-from-top": appearFrom === "top",
    "tooltip-appear-from-right": appearFrom === "right"
  });

  const passthroughProps = {
    ...omit(
      props,
      "forceDisplay",
      "className",
      "content",
      "overlay",
      "children",
      "placement",
      "clickable",
      "appearFrom"
    ),
    className
  };

  return (
    <ReakitStylesProvider unstable_system={reakitSystemStyles}>
      <TooltipReference
        {...tooltipProps}
        {...forcedTooltipProps}
        ref={children.ref}
        {...children.props}
        className={referenceClassName}
      >
        {referenceProps => cloneElement(children, referenceProps)}
      </TooltipReference>

      <ReakitTooltip
        {...tooltipProps}
        {...forcedTooltipProps}
        {...passthroughProps}
        onClick={handleTooltipClicked}
      >
        <div className={tooltipInnerClassName}>
          <TooltipArrow {...tooltipProps} {...forcedTooltipProps} />

          {acceptHTMLContent && <div dangerouslySetInnerHTML={{ __html: content }} />}
          {!acceptHTMLContent && content}
        </div>
      </ReakitTooltip>
    </ReakitStylesProvider>
  );
};

export default Tooltip;
